import React from 'react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-8 text-gray-900">Terms and Conditions</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: January 2025</p>

        <div className="space-y-8 text-gray-700">
          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">1. Introduction</h2>
            <p>
              These Terms and Conditions ("Terms") govern your use of the TESTBOOK LTD driving test booking service. By using our service, you agree to be bound by these Terms.
            </p>
            <p className="mt-4">
              TESTBOOK LTD is a company registered in England and Wales.<br />
              Company Number: 16836503<br />
              Registered Office: 20 Wenlock Road, London, England, N1 7GU
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">2. Service Description</h2>
            <p className="mb-4">
              TESTBOOK LTD provides a <strong>third-party booking assistance service</strong> for practical driving tests in the United Kingdom. 
              We facilitate bookings with the Driver and Vehicle Standards Agency (DVSA) and authorized test centres.
            </p>
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
              <p className="text-gray-800 font-semibold mb-2">Important Disclosure:</p>
              <p className="text-gray-700 mb-2">
                TESTBOOK LTD is <strong>not affiliated with, endorsed by, or connected to the DVSA</strong> or any UK government agency. 
                We are an independent service provider offering booking assistance for a fee.
              </p>
              <p className="text-gray-700">
                You have the option to book your driving test directly through the official DVSA website at{' '}
                <a 
                  href="https://www.gov.uk/book-driving-test" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-900 hover:underline font-medium"
                >
                  www.gov.uk/book-driving-test
                </a>
                {' '}at no additional cost. Our service provides convenience, personal assistance, and additional support throughout the booking process.
              </p>
            </div>
            <p className="mb-4">
              <strong>Our Service Includes:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Personal assistance with the booking process</li>
              <li>Form completion support and guidance</li>
              <li>Availability checks across all UK test centres</li>
              <li>Booking confirmation and reminders</li>
              <li>Customer support throughout the process</li>
              <li>Assistance with rescheduling (subject to DVSA policies)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">3. Eligibility</h2>
            <p className="mb-4">To use our service, you must:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Be at least 17 years old (16 for certain vehicle categories)</li>
              <li>Hold a valid UK provisional or full driving license</li>
              <li>Have passed your theory test (where applicable)</li>
              <li>Meet all DVSA requirements for the test category you are booking</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">4. Booking Process</h2>
            <p className="mb-4">When you make a booking:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You must provide accurate and complete information</li>
              <li>You will receive a booking confirmation via email</li>
              <li>You are responsible for attending your test at the scheduled time</li>
              <li>You must bring all required documentation to your test</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">5. Payment Terms</h2>
            <p className="mb-4">
              Payment is required at the time of booking. We accept major credit and debit cards. All payments are processed securely through our payment provider.
            </p>
            <p>
              Prices displayed on our website include VAT where applicable. Booking fees are non-refundable except as outlined in our Refund Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">6. Cancellations and Changes</h2>
            <p>
              Cancellations and changes to bookings are subject to our Refund Policy. Please refer to the Refund Policy page for detailed information about cancellation terms and fees.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">7. Our Responsibilities</h2>
            <p className="mb-4">TESTBOOK LTD will:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Process your booking request promptly</li>
              <li>Provide confirmation of your booking</li>
              <li>Send reminders about your upcoming test</li>
              <li>Handle your personal data in accordance with UK GDPR</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">8. Your Responsibilities</h2>
            <p className="mb-4">You agree to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate information during booking</li>
              <li>Arrive on time for your scheduled test</li>
              <li>Bring all required documentation and a suitable vehicle (if required)</li>
              <li>Comply with DVSA rules and regulations</li>
              <li>Notify us immediately of any errors in your booking</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">9. Limitation of Liability</h2>
            <p className="mb-4">
              TESTBOOK LTD acts as a booking intermediary. We are not responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The conduct or decisions of DVSA examiners</li>
              <li>Test centre availability or changes</li>
              <li>Your test results or performance</li>
              <li>Events beyond our reasonable control (force majeure)</li>
            </ul>
            <p className="mt-4">
              Our total liability shall not exceed the amount you paid for the booking service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">10. Intellectual Property</h2>
            <p>
              All content on our website, including text, graphics, logos, and software, is the property of TESTBOOK LTD or its licensors and is protected by UK and international copyright laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">11. Data Protection</h2>
            <p>
              We process your personal data in accordance with UK GDPR and our Privacy Policy. By using our service, you consent to such processing and warrant that all data provided is accurate.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">12. Prohibited Activities</h2>
            <p className="mb-4">You must not:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the service for any illegal purpose</li>
              <li>Attempt to make fraudulent bookings</li>
              <li>Interfere with the operation of our website</li>
              <li>Use automated systems to access our service</li>
              <li>Impersonate another person when making a booking</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">13. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your access to our service if you breach these Terms or engage in fraudulent activity.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">14. Changes to Terms</h2>
            <p>
              We may modify these Terms at any time. Changes will be posted on this page with an updated date. Continued use of our service after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">15. Governing Law</h2>
            <p>
              These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">16. Contact Information</h2>
            <p className="mb-4">
              If you have any questions about these Terms, please contact us:
            </p>
            <p>
              TESTBOOK LTD<br />
              20 Wenlock Road, London, England, N1 7GU<br />
              Email: info@testbook.co.uk<br />
              Company Number: 16836503
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">17. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Terms;